import { idadeEmMeses, paraCsv, type FormatoCsv } from '@equiview/domain';
import { traitsV1 } from '@equiview/protocol';
import { indicesDaFicha, ROTULO_INDICE, traitPorCodigo, valoresDaFicha } from './catalogo.js';
import type { Animal, Avaliacao, Manejo, Medida, Observacao, Visita } from './fonte.js';

/** Baixa um texto como arquivo. Funciona offline: o arquivo nasce no aparelho. */
export function baixarArquivo(nome: string, conteudo: string, tipo = 'text/csv;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([conteudo], { type: tipo }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface Contexto {
  animais: readonly Animal[];
  visitas: readonly Visita[];
  avaliacoes: readonly Avaliacao[];
}

const indexar = ({ animais, visitas, avaliacoes }: Contexto) => ({
  animal: new Map(animais.map((a) => [a.id, a])),
  visita: new Map(visitas.map((v) => [v.id, v])),
  ficha: new Map(avaliacoes.map((a) => [a.id, a])),
});

const identificacao = (av: Avaliacao, animal: Animal | undefined, visita: Visita | undefined) => ({
  ficha: av.id,
  visita: visita?.visit_number ?? '',
  tipo_visita: visita?.purpose === 'estudo_repetibilidade' ? 'estudo' : 'rotina',
  data: av.visit_date,
  animal: animal?.name ?? '',
  registro: animal?.abccmm_registry ?? '',
  sexo: animal?.sex ?? '',
  nascimento: animal?.birth_date ?? '',
  idade_meses: animal ? idadeEmMeses(animal.birth_date, av.visit_date) : '',
  nivel: av.protocol_level,
  situacao: av.status,
});

const COLUNAS_DE_IDENTIFICACAO = [
  { chave: 'ficha', titulo: 'ficha' },
  { chave: 'visita', titulo: 'visita' },
  { chave: 'tipo_visita', titulo: 'tipo_visita' },
  { chave: 'data', titulo: 'data' },
  { chave: 'animal', titulo: 'animal' },
  { chave: 'registro', titulo: 'registro' },
  { chave: 'sexo', titulo: 'sexo' },
  { chave: 'nascimento', titulo: 'nascimento' },
  { chave: 'idade_meses', titulo: 'idade_meses' },
  { chave: 'nivel', titulo: 'nivel' },
  { chave: 'situacao', titulo: 'situacao' },
];

/**
 * Formato longo: uma linha por observacao, de todas as sessoes. E o formato do
 * R e o de onde sai o ICC — o mesmo de `v_measurement_long`.
 */
export function csvLongo(ctx: Contexto, observacoes: readonly Observacao[], formato: FormatoCsv): string {
  const idx = indexar(ctx);
  const linhas = observacoes.flatMap((o) => {
    const av = idx.ficha.get(o.assessment_id);
    if (!av) return [];
    return [
      {
        ...identificacao(av, idx.animal.get(av.animal_id), idx.visita.get(av.visit_id)),
        avaliador: o.avaliador,
        rodada: o.rodada,
        metodo: o.metodo,
        medida: o.trait_code,
        medida_nome: traitPorCodigo.get(o.trait_code)?.label ?? '',
        lado: o.side,
        valor: o.not_applicable ? null : o.value_num,
        nao_se_aplica: o.not_applicable,
      },
    ];
  });
  return paraCsv(
    [
      ...COLUNAS_DE_IDENTIFICACAO,
      { chave: 'avaliador', titulo: 'avaliador' },
      { chave: 'rodada', titulo: 'rodada' },
      { chave: 'metodo', titulo: 'metodo' },
      { chave: 'medida', titulo: 'medida' },
      { chave: 'medida_nome', titulo: 'medida_nome' },
      { chave: 'lado', titulo: 'lado' },
      { chave: 'valor', titulo: 'valor' },
      { chave: 'nao_se_aplica', titulo: 'nao_se_aplica' },
    ],
    linhas,
    formato,
  );
}

/** Colunas da Estacao 1, na ordem da ficha de papel. */
const CAMPOS_DE_MANEJO: readonly (keyof Manejo)[] = [
  'weight_kg', 'weight_method', 'bcs_henneke', 'last_feed_time', 'water_access', 'arrived_farm_gt_30d',
  'female_status', 'gestation_months', 'days_postpartum', 'male_status', 'covers_last_week',
  'training_status', 'training_months', 'last_shoeing_date', 'shod_fl', 'shod_fr', 'shod_hl', 'shod_hr',
  'barefoot', 'hoof_condition', 'fitness', 'lameness_limb', 'lameness_aaep', 'dna_sample_collected', 'dna_tube_id',
];

/** Uma linha por ficha (1a medicao), com uma coluna por medida e lado, os indices e a Estacao 1. */
export function csvLargo(
  ctx: Contexto,
  medidas: readonly Medida[],
  formato: FormatoCsv,
  manejos: Readonly<Record<string, Manejo>> = {},
): string {
  const idx = indexar(ctx);
  const porFicha = new Map<string, Medida[]>();
  for (const m of medidas) porFicha.set(m.assessment_id, [...(porFicha.get(m.assessment_id) ?? []), m]);

  const colunasDeMedida = traitsV1
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .flatMap((t) => (t.sided ? [`${t.code}_E`, `${t.code}_D`] : [t.code]));
  const indices = Object.keys(ROTULO_INDICE) as (keyof typeof ROTULO_INDICE)[];

  const linhas = ctx.avaliacoes.map((av) => {
    const daFicha = porFicha.get(av.id) ?? [];
    const valores = valoresDaFicha(daFicha);
    const ind = indicesDaFicha(valores);
    // Origem fora do campo (so o animal real da demonstracao): as medidas, por codigo.
    const codigos = (f: (m: Medida) => boolean) => [...new Set(daFicha.filter(f).map((m) => m.trait_code))].join(' ');
    return {
      ...identificacao(av, idx.animal.get(av.animal_id), idx.visita.get(av.visit_id)),
      medidas_pela_foto: codigos((m) => m.origem === 'foto'),
      medidas_estimadas: codigos((m) => m.origem === 'estimado'),
      medidas_a_conferir: codigos((m) => !!m.conferir),
      medidas_fora_do_protocolo: (idx.animal.get(av.animal_id)?.real?.extras ?? [])
        // Texto livre: o decimal segue o formato escolhido (39,5 no Excel em portugues).
        .map((e) => `${e.rotulo}=${String(e.valor).replace('.', formato.decimal)} ${e.unidade}`)
        .join('; '),
      ...Object.fromEntries(colunasDeMedida.map((c) => [c, valores[c] ?? null])),
      ...Object.fromEntries(indices.map((k) => [`indice_${k}`, ind[k] == null ? null : Number(ind[k]!.toFixed(4))])),
      ...Object.fromEntries(CAMPOS_DE_MANEJO.map((c) => [`manejo_${c}`, manejos[av.id]?.[c] ?? null])),
    };
  });
  return paraCsv(
    [
      ...COLUNAS_DE_IDENTIFICACAO,
      { chave: 'medidas_pela_foto', titulo: 'medidas_pela_foto' },
      { chave: 'medidas_estimadas', titulo: 'medidas_estimadas' },
      { chave: 'medidas_a_conferir', titulo: 'medidas_a_conferir' },
      { chave: 'medidas_fora_do_protocolo', titulo: 'medidas_fora_do_protocolo' },
      ...colunasDeMedida.map((c) => ({ chave: c, titulo: c })),
      ...indices.map((k) => ({ chave: `indice_${k}`, titulo: `indice_${k}` })),
      ...CAMPOS_DE_MANEJO.map((c) => ({ chave: `manejo_${c}`, titulo: `manejo_${c}` })),
    ],
    linhas,
    formato,
  );
}
